import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  LaunchPage,
  MaterialUiTable,
  Tag,
  Spacer,
  TextCapitalized,
  TableTagEnergyRecommendation,
} from "@qivia/ui";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  VehicleElectrification,
  VehicleElectrificationDisplayed,
  VehicleEnergyRecommendation,
} from "./electrificationApi";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";
import styled from "styled-components";
import { selectListAllVehiclesInDb } from "../vehicles/vehiclesSlice";
import {
  selectDepartments,
  getDepartmentsByCompanyAsync,
  selectGetDepartmentsByCompanyStatus,
} from "../departments/departmentsSlice";
import { selectCompany } from "../homeSlice";
import "../../../i18n/i18n";

// TO REMOVE LATER — fake display data, assigned by index (not by uuid) so it renders
// the same in local and demo. Limited to the first 5 vehicles.
const fakeVehicleData: Pick<
  VehicleElectrificationDisplayed,
  "energyRecommendation" | "roadType" | "distanceMax"
>[] = [
  { energyRecommendation: "ELECTRIC", roadType: "CITY", distanceMax: 87 },
  { energyRecommendation: "PLUGIN_HYBRID", roadType: "ROAD", distanceMax: 157 },
  { energyRecommendation: "NOT_ELIGIBLE", roadType: "MIX", distanceMax: 334 },
  {
    energyRecommendation: "INSUFFICIENT_DATA",
    roadType: null,
    distanceMax: null,
  },
  { energyRecommendation: "ELECTRIC", roadType: "CITY", distanceMax: 62 },
];

export const VehiclesTab = () => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const vehiclesListInit = useAppSelector(selectListAllVehiclesInDb);
  const departments = useAppSelector(selectDepartments);
  const company = useAppSelector(selectCompany);
  const getDepartmentsByCompanyStatus = useAppSelector(
    selectGetDepartmentsByCompanyStatus,
  );

  const [listDisplayed, setListDisplayed] = useState<
    VehicleElectrificationDisplayed[] | null
  >(null);

  const pageName = "elecVehicles";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      reference: "size",
      registrationNumber: "size",
      model: "size",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const vehiclesList: VehicleElectrification[] = useMemo(() => {
    const departmentNameByUuid = new Map(
      departments.map((department) => [department.uuid, department.name]),
    );
    const rowsWithId = vehiclesListInit
      .slice(0, fakeVehicleData.length)
      .map((av, index) => {
        const fake = fakeVehicleData[index % fakeVehicleData.length];
        const newDict: VehicleElectrification & { id: string } = {
          uuid: av.uuid,
          id: av.uuid,
          reference: av.reference,
          registrationNumber: av.registrationNumber,
          model: (av.brand ? av.brand : "") + (av.model ? " " + av.model : ""),
          departmentName: av.department
            ? (departmentNameByUuid.get(av.department) ?? null)
            : null,
          energyRecommendation: fake.energyRecommendation,
          roadType: fake.roadType,
          distanceMax: fake.distanceMax,
        };
        return newDict;
      });
    return rowsWithId;
  }, [vehiclesListInit, departments]);

  // TO REMOVE LATER
  useEffect(() => {
    if (vehiclesList) {
      setListDisplayed(vehiclesList);
    }
  }, [vehiclesList]);

  useEffect(() => {
    if (company && getDepartmentsByCompanyStatus === "idle") {
      void dispatch(getDepartmentsByCompanyAsync(company.uuid));
    }
  }, [dispatch, company, getDepartmentsByCompanyStatus]);

  const searchBarProps = {
    values: vehiclesList,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("vehicles.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof VehicleElectrificationDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      reference: {
        type: "string",
        width: 250,
        valueFormatter: (value: string) =>
          value && (
            <StyledRow>
              <StyledCar>
                <img
                  src={Car}
                  style={{ width: "100%", filter: "grayscale(100%)" }}
                  alt="Car"
                />
              </StyledCar>
              <Spacer x={0.5} />
              <Tag
                textColor={colors["colors/text/black"]}
                backgroundColor={
                  colors["colors/surfaces/background/background_level0"]
                }
                text={value}
                borderColor={colors["colors/borders/cells/cells"]}
              />
            </StyledRow>
          ),
      },
      registrationNumber: {
        type: "string",
      },
      model: {
        type: "string",
      },
      departmentName: {
        type: "string",
        width: 250,
      },
      energyRecommendation: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) =>
          value && (
            <StyledRow>
              <TableTagEnergyRecommendation
                energyRecommendation={value as VehicleEnergyRecommendation}
                text={t(`electrification.energyRecommendation.${value}`)}
              />
            </StyledRow>
          ),
      },
      roadType: {
        type: "string",
        width: 160,
        valueFormatter: (value: string) =>
          value ? (
            <TextCapitalized>
              {t(`electrification.roadType.${value}`)}
            </TextCapitalized>
          ) : (
            "-"
          ),
      },
      distanceMax: {
        type: "number",
        valueFormatter: (value: number) =>
          value != null ? `${value} km` : "-",
      },
    };
  }, [t]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "vehicles") {
    return;
  }

  return (
    <MaterialUiTable<
      keyof VehicleElectrificationDisplayed,
      VehicleElectrificationDisplayed
    >
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const StyledCar = styled.div`
  width: 2rem;
`;
